import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import VetrichLogo from "@/components/VetrichLogo";

export default function Login() {
  const { login, user, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome to Vetrich");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left panel: brand + image */}
      <div className="relative hidden lg:block auth-bg">
        <div className="absolute inset-0 grid-tile opacity-40" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="inline-flex self-start bg-white rounded-md shadow-lg p-3 pr-5 items-center">
            <VetrichLogo size={64} tone="dark" data-testid="login-brand-logo-left" />
          </div>

          <div className="max-w-lg">
            <div className="eyebrow text-white/70 mb-4">Enterprise ERP · GST-Ready</div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight leading-[1.02]">
              Smart Accounting.<br />
              <span className="text-white/70">Smarter Business.</span>
            </h1>
            <p className="text-white/80 mt-6 text-[15px] leading-relaxed max-w-md">
              A complete ERP for manufacturers, traders, retailers and SMEs — accounting,
              inventory, GST filing, billing and analytics in one uncompromising platform.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 text-[11px] uppercase tracking-widest text-white/70">
              <div><div className="text-white text-2xl font-heading font-bold">100%</div>GST Compliant</div>
              <div><div className="text-white text-2xl font-heading font-bold">Multi</div>Company</div>
              <div><div className="text-white text-2xl font-heading font-bold">Real-Time</div>Dashboards</div>
            </div>
          </div>
          <div className="text-[11px] text-white/60 tracking-wider">© 2026 Vetrich Technologies · v1.0</div>
        </div>
      </div>

      {/* Right panel: form */}
      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm">
          {/* Compact brand mark — visible on mobile where left panel is hidden */}
          <div className="mb-8 flex items-center justify-between">
            <VetrichLogo size={56} tone="dark" data-testid="login-brand-logo" />
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">ERP · v1.0</div>
          </div>
          <div className="eyebrow mb-3">Sign in</div>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-1">Welcome back.</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Use your Vetrich admin credentials to access the ERP console.
          </p>

          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="eyebrow">Login ID</Label>
              <Input
                id="email" type="text" required autoComplete="username"
                placeholder="Enter your login ID"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-11" data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="eyebrow">Password</Label>
              <Input
                id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-11" data-testid="login-password"
              />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 group rounded-md" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight weight="bold" size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <div className="text-center">
              <a href="/forgot-password"
                className="text-[12px] text-muted-foreground hover:text-primary underline underline-offset-2 transition"
                data-testid="forgot-password-link">
                Forgot password?
              </a>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-border text-[12px] text-muted-foreground flex items-start gap-2.5">
            <ShieldCheck weight="regular" size={16} className="mt-0.5 shrink-0" />
            <span>Session secured with HttpOnly cookies + JWT. Role-based access is enforced server-side.</span>
          </div>
        </form>
      </div>
    </div>
  );
}
